'use client';

import { useMemo } from 'react';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Address } from 'viem';

import LockIcon from '@/assets/icons/account/lock.svg';
import Subtract from '@/assets/icons/subtract.svg';
import { CountdownTimer } from '@/components/shared/Countdown';
import { HoverPopover } from '@/components/shared/HoverPopover';
import { H4, P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { usePoolsTokenBalances } from '@/hooks/PoolsToken/usePoolsTokenBalances';
import { cn } from '@/lib/utils/cn';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { useCountdownRenderer } from '@/lib/utils/getCountdownRenderer';
import { getPresaleEndMs } from '@/lib/utils/getPresaleEndMs';
import { C_DECIMAL_PLACES } from '@/ts/constants/C_DECIMAL_PLACES';
import { T_TokenPool } from '@/ts/types/T_Profile';

export type T_TokenPoolCardProps = {
  pool: T_TokenPool;
  hasAnyActivePresale?: boolean;
};

function TokenPoolCard({
  pool: {
    Icon,
    ticker,
    name: _name,
    myTokens: myTokensProp,
    value: valueProp,
    className,
    presaleEndDate,
    poolAddress,
    tokenAddress,
    tokenDecimals,
    balanceDecimal,
    currentPriceDecimal
  },
  hasAnyActivePresale = false
}: T_TokenPoolCardProps) {
  const t = useTranslations();
  const tPools = useTranslations('pools');
  const presaleEndMs = useMemo(
    () => getPresaleEndMs(presaleEndDate),
    [presaleEndDate]
  );

  // Fetch balances if pool addresses are provided
  const { formattedTokenBalance, formattedUsdcBalance, tokenBalance } =
    usePoolsTokenBalances({
      poolContractAddress: poolAddress as Address | undefined,
      tokenAddress: tokenAddress as Address | undefined,
      tokenDecimals: tokenDecimals || C_DECIMAL_PLACES
    });

  // Use fetched balances if available, otherwise use props
  const myTokens = useMemo(() => {
    if (
      poolAddress &&
      tokenAddress &&
      tokenBalance &&
      formattedTokenBalance &&
      parseFloat(formattedTokenBalance) > 0
    ) {
      return formatNumberWithCommasAndDecimals(
        formattedTokenBalance,
        C_DECIMAL_PLACES
      );
    }
    // Format myTokensProp with commas if provided
    if (myTokensProp) {
      const numericValue = parseFloat(myTokensProp);
      if (!isNaN(numericValue)) {
        return formatNumberWithCommasAndDecimals(
          myTokensProp,
          C_DECIMAL_PLACES
        );
      }
      return myTokensProp;
    }
    return '0';
  }, [
    poolAddress,
    tokenAddress,
    formattedTokenBalance,
    tokenBalance,
    myTokensProp
  ]);

  const value = useMemo(() => {
    // Calculate from balanceDecimal * currentPriceDecimal if available
    if (balanceDecimal && currentPriceDecimal) {
      const balance = parseFloat(balanceDecimal);
      const price = parseFloat(currentPriceDecimal);

      if (
        !isNaN(balance) &&
        !isNaN(price) &&
        isFinite(balance) &&
        isFinite(price) &&
        balance >= 0 &&
        price >= 0
      ) {
        const totalValue = balance * price;
        if (isFinite(totalValue) && totalValue >= 0) {
          return `$${formatNumberWithCommasAndDecimals(
            totalValue.toString(),
            C_DECIMAL_PLACES
          )}`;
        }
      }
      // If calculation is invalid, return $0.00
      return '$0.00';
    }

    return valueProp || '$0.00';
  }, [valueProp, balanceDecimal, currentPriceDecimal]);
  const countdownRenderer = useCountdownRenderer(tPools);

  const isPresaleEnded = new Date(presaleEndMs || 0) < new Date();

  // Extract slug from ticker (e.g., 'TxBTC' -> 'btc', 'BTC' -> 'btc')
  const slug = useMemo(() => {
    if (!ticker) return '';
    // Remove 'Tx' prefix if present and convert to lowercase
    const cleaned = ticker.replace(/^Tx/i, '').toLowerCase();
    return cleaned;
  }, [ticker]);
  return (
    <div
      className={cn(
        className,
        'flex flex-col rounded-2xl w-full min-w-69 sm:min-w-75 banner-gradient-animation'
      )}
    >
      <div className="p-6 gap-6 flex flex-col rounded-2xl">
        <div className="flex items-center gap-4">
          <Icon />
          <div className="">
            <H4 className="mb-0.5 max-w-35">{ticker}</H4>
          </div>
        </div>
        <div className="flex gap-5">
          <div className="flex-1">
            <P className="text-sm font-medium">
              {t('profile.tokenPoolCard.myTokens')}
            </P>
            <P className="text-lg font-bold">{myTokens}</P>
          </div>
          <div className="flex-1">
            <P className="text-sm font-medium">
              {t('profile.tokenPoolCard.myValue')}
            </P>
            <P className="text-lg font-bold">{value}</P>
          </div>
        </div>
        <Button asChild className="w-full" variant="secondary">
          <Link href={`/profile/pools/${slug}`}>
            {t('profile.tokenPoolCard.tokenHistory')}
          </Link>
        </Button>
      </div>
      {hasAnyActivePresale && (
        <div className="bg-background/30 py-2 px-6 rounded-bl-2xl rounded-br-2xl flex items-center justify-center gap-3 min-h-11">
          {presaleEndMs !== null && !isPresaleEnded && (
            <>
              <LockIcon className="w-6 h-6" />
              <CountdownTimer
                date={presaleEndMs}
                renderer={countdownRenderer}
                className="flex justify-center text-center"
              />
              <HoverPopover
                trigger={
                  <Subtract className="cursor-pointer text-secondary hover:text-primary-hover active:text-primary-hover focus:text-primary-hover touch-manipulation transition-colors" />
                }
                triggerAriaLabel="More info"
                side="bottom"
                align="end"
                sideOffset={10}
                alignOffset={-10}
              >
                <P className="text-xs font-semibold leading-normal text-popover-foreground">
                  {tPools('unlockDescription')}
                </P>
              </HoverPopover>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default TokenPoolCard;
